import React from "react";
import { Box, Card, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import TrendIndicator, { TREND_COLORS } from "./TrendIndicator";
import { formatMetricValue, isCurrencyUnit, unitLabel } from "../utils/performanceTrends";

// Ported verbatim from startup-web (views/performance/components/MetricChartCard.js).
const BORDER = "#EAECF0";

const TrendChip = ({ change }) => {
    if (!change) return null;
    const up = change.direction !== "down";
    const bg = change.direction === "flat" ? "#F2F4F7" : up ? "#ECFDF3" : "#FEF3F2";
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", background: bg, borderRadius: "6px", px: 0.75, py: 0.25 }}>
            <TrendIndicator change={change} size="0.7rem" />
        </Box>
    );
};

// One chart per configured metric for the Graphs-tab grid. Currency metrics
// render as bars, everything else (Count / Months / Percentage) as an area line,
// matching the design.
const MetricChartCard = ({ metric, series, color = "#7A5AF8", fullWidth = false }) => {
    const { name, unit, value, change } = metric;
    const points = series?.points || [];
    const months = points.map((p) => p.month);
    const values = points.map((p) => (p.value === null || p.value === undefined ? null : p.value));
    const isBar = isCurrencyUnit(unit);

    const yValueFormatter = (v) => (v === null || v === undefined ? "" : formatMetricValue(v, unit));
    const hasData = points.length > 0;

    // A band scale splits the plot evenly between categories, so with one or two
    // months each bar takes half the card and reads as a solid block of colour
    // rather than a chart. Widen the gap either side of the bar as the number of
    // months drops, so a single month renders as a bar, not a wall.
    const gapRatios = fullWidth
        ? [0.94, 0.9, 0.84, 0.7, 0.45]
        : [0.82, 0.68, 0.55, 0.4, 0.2];
    const categoryGapRatio =
        points.length <= 1 ? gapRatios[0]
            : points.length === 2 ? gapRatios[1]
                : points.length === 3 ? gapRatios[2]
                    : points.length <= 5 ? gapRatios[3]
                        : gapRatios[4];

    const commonAxis = {
        height: 240,
        margin: { top: 16, right: 12, bottom: 24, left: 48 },
        slotProps: { legend: { hidden: true } },
        grid: { horizontal: true },
        sx: {
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { display: "none" },
            "& .MuiChartsGrid-line": { stroke: "#F2F4F7" },
            "& .MuiChartsAxis-tickLabel": { fontSize: "0.65rem", fill: "#98A2B3" },
        },
    };

    return (
        <Card sx={{ boxShadow: "none", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "1rem 1.25rem", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#101828" }}>
                    {name} {unitLabel(unit)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", background: "#F2F4F7", borderRadius: "6px", px: 0.75, py: 0.25 }}>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#344054" }}>{value}</Typography>
                    </Box>
                    <TrendChip change={change} />
                </Box>
            </Box>

            {hasData ? (
                isBar ? (
                    <BarChart
                        {...commonAxis}
                        xAxis={[{ scaleType: "band", data: months, categoryGapRatio, barGapRatio: 0.2 }]}
                        yAxis={[{ valueFormatter: yValueFormatter }]}
                        series={[{ data: values, color, valueFormatter: yValueFormatter }]}
                        borderRadius={6}
                    />
                ) : (
                    <LineChart
                        {...commonAxis}
                        xAxis={[{ scaleType: "point", data: months }]}
                        yAxis={[{ valueFormatter: yValueFormatter }]}
                        series={[{ data: values, color, area: true, curve: "natural", showMark: true, valueFormatter: yValueFormatter }]}
                        sx={{
                            ...commonAxis.sx,
                            "& .MuiAreaElement-root": { fill: `${color}22` },
                        }}
                    />
                )
            ) : (
                <Box sx={{ height: 240, display: "grid", placeItems: "center", color: TREND_COLORS.flat }}>
                    No Data Available
                </Box>
            )}
        </Card>
    );
};

export default MetricChartCard;
